import type { OrganisationType } from "../../../../clients/graphqlTypes.js";
import type { OnboardingFlowStage } from "./OnboardingFlowModal.js";

export type OnboardingStepId = "business" | "property" | "trades" | "done";

export type OnboardingStepStatus = "completed" | "current" | "upcoming";

export type OnboardingStep = {
  id: OnboardingStepId;
  status: OnboardingStepStatus;
};

// Only a CRAFTSMAN describes a business and declares trades; every other
// organisation type (a property owner) describes its property and is done.
const stepIdsFor = (organisationType: OrganisationType): OnboardingStepId[] =>
  organisationType === "CRAFTSMAN"
    ? ["business", "trades", "done"]
    : ["property", "done"];

/**
 * The stage a completed step leads back to from the rail; `null` for the goal,
 * which is only reached, never revisited.
 */
export const getOnboardingStepStage = (
  id: OnboardingStepId,
  organisationType: OrganisationType
): OnboardingFlowStage | null => {
  switch (id) {
    case "business":
    case "property":
      return "form";
    case "trades":
      return organisationType === "CRAFTSMAN" ? "verticals" : null;
    case "done":
      return null;
  }
};

const stepIdOfStage = (
  stage: OnboardingFlowStage,
  organisationType: OrganisationType
): OnboardingStepId | null => {
  switch (stage) {
    case "intro":
      return null;
    case "form":
      return organisationType === "CRAFTSMAN" ? "business" : "property";
    case "verticals":
      return "trades";
    case "success":
      // The goal ("Ready to go") is reached here, so it is not a current step.
      return null;
  }
};

/**
 * The steps shown in the sidebar, each marked relative to the current stage;
 * on the intro stage none has started, so all are upcoming, and on the success
 * stage all are completed, the goal "done" included. `null` for a stage
 * the organisation type never visits (e.g. `verticals` reached by a CLIENT
 * through a typed URL).
 */
export function getOnboardingSteps(
  stage: OnboardingFlowStage,
  organisationType: OrganisationType
): OnboardingStep[] | null {
  const stepId = stepIdOfStage(stage, organisationType);
  const ids = stepIdsFor(organisationType);
  if (stage === "intro") {
    return ids.map(id => ({ id, status: "upcoming" }));
  }
  if (stage === "success") {
    return ids.map(id => ({ id, status: "completed" }));
  }
  const currentIndex = stepId === null ? -1 : ids.indexOf(stepId);
  if (currentIndex === -1) {
    return null;
  }
  return ids.map((id, index) => ({
    id,
    status:
      index < currentIndex
        ? "completed"
        : index === currentIndex
          ? "current"
          : "upcoming",
  }));
}

/**
 * The 1-based position for the compact "Step N of M" label, or `null` without
 * a current step (intro, success, unvisited stage).
 */
export function getOnboardingStepProgress(
  stage: OnboardingFlowStage,
  organisationType: OrganisationType
): { current: number; total: number } | null {
  const steps = getOnboardingSteps(stage, organisationType);
  const current = steps?.findIndex(step => step.status === "current") ?? -1;
  if (!steps || current === -1) {
    return null;
  }
  return { current: current + 1, total: steps.length };
}
